<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { RollingNumberProps } from './types';

const props = withDefaults(defineProps<RollingNumberProps>(), {
  value: 0,
  duration: 1000,
  animated: true,
  precision: 1,
  thousandSeparator: false,
  customClass: undefined,
});

// 当前显示的值
const displayValue = ref<number>(0);
// 是否正在动画中
const isAnimating = ref(false);
// 最新的目标值
const targetValue = ref<number>(0);
// 用于取消之前的动画
const animationFrameId = ref<number | null>(null);

// 将字符串转换为数字
const parseValue = (val: number | string): number => {
  if (typeof val === 'number') return val;
  const parsed = parseFloat(val);
  return isNaN(parsed) ? 0 : parsed;
};

// 格式化数字
const formatNumber = (num: number): string => {
  if (props.formatter) {
    return props.formatter(num);
  }

  let formatted = num.toString();

  // 处理小数位
  if (props.precision !== undefined) {
    formatted = num.toFixed(props.precision);
  }

  // 移除末尾多余的零和小数点
  formatted = formatted.replace(/\.?0+$/, '');

  // 处理千分位分隔符
  if (props.thousandSeparator) {
    const parts = formatted.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    formatted = parts.join('.');
  }

  return formatted;
};

// 动画函数
const animateValue = () => {
  if (!props.animated || displayValue.value === targetValue.value || props.duration <= 0) {
    displayValue.value = targetValue.value;
    isAnimating.value = false;
    return;
  }

  isAnimating.value = true;
  const startTime = performance.now();
  const start = displayValue.value;
  const end = targetValue.value;
  const difference = end - start;

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / props.duration, 1);

    // 使用缓动函数（ease-out）
    const easeOut = 1 - Math.pow(1 - progress, 3);
    const currentValue = start + difference * easeOut;

    // 在动画过程中控制精度，避免显示过多小数位
    displayValue.value = parseFloat(currentValue.toFixed(props.precision));

    if (progress < 1) {
      // 检查是否有新的目标值
      if (targetValue.value !== end) {
        // 如果目标值已更改，重新开始动画
        if (animationFrameId.value) {
          cancelAnimationFrame(animationFrameId.value);
        }
        animationFrameId.value = requestAnimationFrame(animateValue);
        return;
      }
      animationFrameId.value = requestAnimationFrame(animate);
    } else {
      // 使用 targetValue 而非 end，避免 value 在动画末尾变更时显示 stale 值
      displayValue.value = targetValue.value;
      isAnimating.value = false;
    }
  };

  animationFrameId.value = requestAnimationFrame(animate);
};

// 监听值的变化
watch(
  () => props.value,
  newVal => {
    const newNum = parseValue(newVal);
    targetValue.value = newNum;

    // 如果不在动画中，直接开始动画
    if (props.animated && !isAnimating.value) {
      nextTick(() => {
        animateValue();
      });
    } else if (!props.animated) {
      // 如果未启用动画，直接设置显示值
      displayValue.value = newNum;
    }
  },
);

// 格式化后的显示文本
const displayText = computed(() => formatNumber(displayValue.value));

// 初始化
onMounted(() => {
  const initialValue = parseValue(props.value);
  displayValue.value = initialValue;
  targetValue.value = initialValue;
});

// 组件卸载时取消动画，避免更新已销毁的组件
onBeforeUnmount(() => {
  if (animationFrameId.value !== null) {
    cancelAnimationFrame(animationFrameId.value);
    animationFrameId.value = null;
  }
});
</script>

<template>
  <span :class="['rolling-number', props.customClass]"> {{ displayText }} </span>
</template>

<style scoped lang="scss">
.rolling-number {
  display: inline-block;
  font-variant-numeric: tabular-nums; // 确保数字等宽，避免滚动时抖动
}
</style>
